// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * Getting images in.
 *
 * Four routes, because no single one covers the browsers people actually use:
 *
 *   showDirectoryPicker      Chromium desktop only. Firefox and Safari have never shipped it.
 *   webkitGetAsEntry         Drag a folder. The broadest support of the four — Chrome 13,
 *                            Firefox 50, Safari 11.1, Safari iOS 11.3.
 *   <input webkitdirectory>  Desktop everywhere; on iOS it only genuinely works from Safari 18.4
 *                            (11.3-18.3 accept the attribute and silently ignore it, WebKit bug
 *                            271705), which is why it is not the primary route on a phone.
 *   a dropped .zip           The mobile path, and the one the bundled sample uses.
 */

import { unzip, type Unzipped } from 'fflate';

/** Extensions a browser will actually decode via createImageBitmap. */
const DECODABLE = new Set(['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp', 'avif']);

/**
 * HEIC is the elephant in an iPhone photo library and NO browser decodes it.
 *
 * Safari cannot `createImageBitmap` a HEIC despite the platform having a decoder, and neither can
 * Chrome or Firefox. Rather than fail on a third of somebody's camera roll with a generic error,
 * these are counted, named, and pointed at the sibling tool that converts them.
 */
const HEIC = new Set(['heic', 'heif']);

export interface IngestedFile {
  readonly path: string;
  readonly file: File;
}

export interface IngestResult {
  readonly files: readonly IngestedFile[];
  readonly skippedHeic: readonly string[];
  readonly skippedOther: readonly string[];
}

function extensionOf(path: string): string {
  const base = path.split('/').pop() ?? path;
  const dot = base.lastIndexOf('.');
  return dot > 0 ? base.slice(dot + 1).toLowerCase() : '';
}

/** Ignore macOS resource forks and the junk directories every archive of photos contains. */
function isJunk(path: string): boolean {
  const base = path.split('/').pop() ?? path;
  return (
    base.startsWith('._') ||
    base === '.DS_Store' ||
    base === 'Thumbs.db' ||
    path.startsWith('__MACOSX/') ||
    path.includes('/__MACOSX/')
  );
}

export function classify(entries: readonly IngestedFile[]): IngestResult {
  const files: IngestedFile[] = [];
  const skippedHeic: string[] = [];
  const skippedOther: string[] = [];
  for (const e of entries) {
    if (isJunk(e.path)) continue;
    const ext = extensionOf(e.path);
    if (DECODABLE.has(ext)) files.push(e);
    else if (HEIC.has(ext)) skippedHeic.push(e.path);
    else skippedOther.push(e.path);
  }
  files.sort((a, b) => a.path.localeCompare(b.path, 'en', { numeric: true }));
  return { files, skippedHeic, skippedOther };
}

export function fromFileList(list: FileList | readonly File[]): IngestResult {
  const entries: IngestedFile[] = [];
  for (const f of Array.from(list)) {
    // webkitRelativePath is populated by <input webkitdirectory> and is the only way to recover
    // the folder structure the user picked.
    const rel = (f as File & { webkitRelativePath?: string }).webkitRelativePath;
    entries.push({ path: rel && rel.length > 0 ? rel : f.name, file: f });
  }
  return classify(entries);
}

/**
 * A dropped ZIP, expanded in memory.
 *
 * `unzip` rather than `unzipSync` so the worker thread is not blocked, and the whole archive does
 * land in memory — which is fine at the sizes a ZIP arrives at (someone with 4 GB of photos hands
 * over a folder, not a zip) and is the reason the size is checked before the work starts.
 */
export async function fromZip(blob: Blob): Promise<IngestResult> {
  const buf = new Uint8Array(await blob.arrayBuffer());
  const entries: Unzipped = await new Promise((resolve, reject) => {
    unzip(buf, (err, data) => (err ? reject(err) : resolve(data)));
  });
  const out: IngestedFile[] = [];
  for (const [path, bytes] of Object.entries(entries)) {
    if (path.endsWith('/') || bytes.length === 0) continue;
    const name = path.split('/').pop() ?? path;
    // `new File` copies, so the archive buffer can be released as soon as this returns.
    out.push({ path, file: new File([bytes as BlobPart], name) });
  }
  return classify(out);
}

/**
 * Walk a dropped folder via the legacy FileSystem entries API.
 *
 * `webkitGetAsEntry` has to be called SYNCHRONOUSLY while handling the drop event — the
 * DataTransfer items are neutered the moment the handler yields — so the caller collects the
 * entries first and hands them here.
 */
export async function fromEntries(entries: readonly FileSystemEntry[]): Promise<IngestResult> {
  const out: IngestedFile[] = [];

  const readDir = (reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> =>
    new Promise((resolve, reject) => reader.readEntries(resolve, reject));

  const walk = async (entry: FileSystemEntry, prefix: string): Promise<void> => {
    if (entry.isFile) {
      const file = await new Promise<File>((resolve, reject) =>
        (entry as FileSystemFileEntry).file(resolve, reject),
      );
      out.push({ path: prefix + entry.name, file });
      return;
    }
    const reader = (entry as FileSystemDirectoryEntry).createReader();
    // readEntries returns at most 100 at a time and signals the end with an empty array. Calling
    // it once — which every naive implementation does — silently truncates a folder of 3,000
    // photographs to the first 100.
    for (;;) {
      const batch = await readDir(reader);
      if (batch.length === 0) break;
      for (const child of batch) await walk(child, `${prefix + entry.name}/`);
    }
  };

  for (const e of entries) await walk(e, '');
  return classify(out);
}

/**
 * A directory handle that can be iterated.
 *
 * `entries()` has been in Chromium since the API shipped, but the DOM lib's
 * `FileSystemDirectoryHandle` still does not declare it — so this narrow interface is the
 * alternative to an `any`.
 */
type IterableDirectoryHandle = FileSystemDirectoryHandle & {
  entries(): AsyncIterableIterator<[string, FileSystemHandle]>;
};

/** Chromium's directory picker. Recursive, and the only route that can also stream the export back. */
export async function fromDirectoryHandle(dir: FileSystemDirectoryHandle): Promise<IngestResult> {
  const out: IngestedFile[] = [];
  const walk = async (handle: FileSystemDirectoryHandle, prefix: string): Promise<void> => {
    for await (const [name, child] of (handle as IterableDirectoryHandle).entries()) {
      if (child.kind === 'file') {
        out.push({ path: prefix + name, file: await (child as FileSystemFileHandle).getFile() });
      } else {
        await walk(child as FileSystemDirectoryHandle, `${prefix + name}/`);
      }
    }
  };
  await walk(dir, '');
  return classify(out);
}

export function totalBytes(files: readonly IngestedFile[]): number {
  let n = 0;
  for (const f of files) n += f.file.size;
  return n;
}
